import * as React from "react";
import "../billing/account/create/responsive.css";
import { Button, notification, Tag } from "antd";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { request } from "../../services/cpln";
import { getBillingErrorMessage } from "../../services/utils";
import { useBlocker, useNavigate } from "react-router-dom";
import { OrgCreate, OrgCreateInput } from "../../schema/types/org/create";
import { getDefaultOrgCreateInput } from "./utils";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchOrgs, selectAccountSummaries } from "../../store/userDataSlice";
import {
  formInitialize,
  formSetValue,
  formTriggerValidation,
  formUninitialize,
  useCpFormAnyValueId,
  useCpFormSubmit,
} from "../gvc/testNewForm/utils";
import { CpAutoComplete } from "../gvc/testNewForm/CpAutoComplete";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpInput } from "../gvc/testNewForm/CpInput";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpArrayObject } from "../gvc/testNewForm/CpArrayObject";
import { CpArrayItemOrgInvitee } from "./CpArrayItemOrgInvitee";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormPrompt } from "../../components/antd/form/CpFormPrompt";
import { getValidatorCreate } from "./createValidator";
import { sentryLog } from "../../tools/sentry";

export const OrgCreateForm: React.FC = () => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    formInitialize(formIdRef.current, getDefaultOrgCreateInput());
    setIsInitialized(true);
    return () => {
      formUninitialize(formIdRef.current);
    };
  }, []);

  const userEmail = useAppSelector((state) => state.user.email);
  const accountSummaries = useAppSelector(selectAccountSummaries);

  const accountId = useCpFormAnyValueId(formIdRef.current, `values.accountId`);
  const invitees = useCpFormAnyValueId(formIdRef.current, `values.invitees`) || [];

  const submitting = useCpFormAnyValueId(formIdRef.current, "meta.submitting");

  const basePath = useBasePath("/org/-create/*", true, "home");

  const navigate = useNavigate();

  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/org/-create`) &&
      !nextLocation.pathname.includes(`/org/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  const [roles, setRoles] = React.useState<string[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = React.useState(false);
  const [tou, setTou] = React.useState<boolean>(false);
  const [isLoadingTou, setIsLoadingTou] = React.useState(false);

  const rolesError = React.useMemo(() => {
    if (isLoadingRoles) {
      return "";
    }
    if (!roles.includes("billing_admin") && !roles.includes("org_creator")) {
      return `You must have one of ["billing_admin", "org_creator"] roles in this account to be able to create an org with this account.`;
    }
    return "";
  }, [roles, isLoadingRoles]);
  const touError = React.useMemo(() => {
    if (isLoadingTou) {
      return "";
    }
    if (!tou) {
      return `You must accept Terms of Use for this account to be able to create an org with this account.`;
    }
    return "";
  }, [tou, isLoadingTou]);

  React.useEffect(() => {
    if (!accountId) {
      return;
    }

    fetchRoles();
    fetchTou();
    fetchUsers();
  }, [accountId]);

  React.useEffect(() => {
    const _invitees = invitees.filter(Boolean);
    if (!_invitees.some((i) => i.value === userEmail)) {
      _invitees.push({ id: uuidv4(), value: userEmail });
    }
    formSetValue(formIdRef.current, `invitees`, _invitees);
    formTriggerValidation(formIdRef.current);
  }, [roles]);

  async function fetchRoles() {
    try {
      setIsLoadingRoles(true);
      const { data } = await request({ service: "billing-ng", url: `/account/${accountId}/user/self` });
      if (data.admin === true) {
        setRoles(["billing_admin"]);
      } else {
        setRoles(data.roles || []);
      }
      setIsLoadingRoles(false);
    } catch (e) {
      setIsLoadingRoles(false);
      setRoles([]);
    }
  }

  async function fetchTou() {
    try {
      setIsLoadingTou(true);
      await request({ service: "billing-ng", url: `/account/${accountId}/tou` });
      setTou(true);
      setIsLoadingTou(false);
    } catch (e) {
      setIsLoadingTou(false);
      setTou(false);
    }
  }

  async function fetchUsers() {
    try {
      const { data } = await request({ service: "billing-ng", url: `/account/${accountId}/user` });
      formSetValue(formIdRef.current, `meta.users`, data.items);
    } catch (e) {
      formSetValue(formIdRef.current, `meta.users`, []);
    }
  }

  function getOrgCreateBody(input: OrgCreateInput): OrgCreate {
    const name = input.name;
    const description = input.description || input.name;
    const body: any = {
      org: {
        name,
        description,
      },
      invitees: input.invitees.map((i) => i.value),
    };
    return body;
  }

  const validatorRef = React.useRef(getValidatorCreate(formIdRef.current));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formIdRef.current, onSubmit: handleSubmit, validationFn: validator });

  async function handleSubmit(input: OrgCreateInput) {
    if (rolesError || touError) {
      return;
    }

    ignoreBlockerRef.current = true;

    try {
      await request({
        method: "post",
        service: "billing-ng",
        url: `/account/${accountId}/org`,
        body: getOrgCreateBody(input),
      });
      dispatch(fetchOrgs());
      navigate(`/home/org`);
    } catch (e) {
      notification.warning({
        message: "Failed",
        description: getBillingErrorMessage(e),
      });
    }
  }

  const selectedAccount = accountSummaries.find((as) => as.id === accountId);

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ submit: submit, id: formIdRef.current }}>
      <BasePathContext.Provider value={basePath}>
        <div style={{ maxWidth: 900, margin: "auto" }}>
          <div className="font-semibold mb-6 text-xl">Create Org</div>
          <div className={accountId ? "mb-10" : "mb-6"}>
            <CpLabel name={`accountId`}>Billing Account ID</CpLabel>
            <CpAutoComplete
              name={`accountId`}
              style={{ width: "100%" }}
              options={accountSummaries.map((as) => ({
                label: (
                  <div className="py-2">
                    {as.accountName ? (
                      <>
                        <div className="-mb-1 text-xs">Account Name</div>
                        <div className="font-semibold">{as.accountName}</div>
                      </>
                    ) : null}
                    {as.fullName && as.fullName !== as.accountName ? (
                      <>
                        <div className="-mb-1 text-xs">Full Name</div>
                        <div className="font-semibold">{as.fullName}</div>
                      </>
                    ) : null}
                    {as.id ? (
                      <>
                        <div className="-mb-1 text-xs">Billing Account ID</div>
                        <div className="font-semibold">{as.id}</div>
                      </>
                    ) : null}
                  </div>
                ),
                value: as.id,
              }))}
              filterOption={(inputValue, option) => {
                const as = accountSummaries.find((as) => as.id === option?.value);
                if (!as) {
                  return false;
                }

                const value = inputValue.toLowerCase();
                if (as.accountName?.toLowerCase().includes(value)) {
                  return true;
                }
                if (as.fullName?.toLowerCase().includes(value)) {
                  return true;
                }
                if (as.company?.toLowerCase().includes(value)) {
                  return true;
                }
                if (as.id?.toLowerCase().includes(value)) {
                  return true;
                }
                return false;
              }}
            />
            <CpErrorWarning name={`accountId`} />
            {!!accountId && (rolesError || touError) ? (
              <div className="-mt-4 mb-2 text-sm color-danger">{rolesError || touError}</div>
            ) : null}
            {!!accountId && selectedAccount?.accountName ? (
              <>
                <div className="-mb-1 text-xs">Account Name</div>
                <div className="font-semibold mb-2">{selectedAccount.accountName}</div>
              </>
            ) : null}
            {!!accountId && selectedAccount?.fullName && selectedAccount?.fullName !== selectedAccount?.accountName ? (
              <>
                <div className="-mb-1 text-xs">Full Name</div>
                <div className="font-semibold mb-2">{selectedAccount.fullName}</div>
              </>
            ) : null}
            {!!accountId && !isLoadingRoles && roles.length > 0 ? (
              <>
                <div className="text-xs">Roles</div>
                <div className="flex flex-wrap gap-1">
                  {roles.map((role) => (
                    <Tag key={role}>{role}</Tag>
                  ))}
                </div>
              </>
            ) : null}
          </div>
          <CpFormItem>
            <CpLabel name={`name`}>Org Name</CpLabel>
            <CpInput name={`name`} />
            <CpErrorWarning name={`name`} />
          </CpFormItem>
          <CpFormItem>
            <CpLabel name={`description`}>Org Description</CpLabel>
            <CpInput name={`description`} />
            <CpErrorWarning name={`description`} />
          </CpFormItem>
          <CpArrayObject label="Org Admins" factoryFn={() => ({ id: uuidv4(), value: "" })} name="invitees">
            <CpArrayItemOrgInvitee />
          </CpArrayObject>
          <div className="flex gap-4 mt-6">
            <Button
              htmlType={`button`}
              disabled={submitting}
              loading={submitting}
              block
              type={"default"}
              danger
              onClick={() => {
                ignoreBlockerRef.current = true;
                navigate(`/home/org`);
              }}
            >
              Cancel
            </Button>
            <Button onClick={submit} disabled={submitting} loading={submitting} block type={"primary"}>
              Create Org
            </Button>
          </div>
          <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
          <CpFormValidator validationFn={validator} />
        </div>
      </BasePathContext.Provider>
    </CpFormContext.Provider>
  );
};
